import * as React from 'react';
import { Box, Typography, CircularProgress, Tabs, Tab } from '@mui/material';
import { useParams } from 'react-router-dom';
import { TeamSeason, LeagueSeason, League, LeaguePhase, LeagueGame, Team, Player } from '../../../sdk/model';
import DivisionStandings from './DivisionStandings';
import OverallStandings from './OverallStandings';
import PlayoffBracket from '../league/PlayoffBracket';
import TeamDisplay from './TeamDisplay';
import { useAppSelector } from '../../../app/hooks';
import { usePlayerDataLoadFlat } from '../../util/PlayerDataUtil';
import { loadGameRosterFromQuickstats } from '../../util/GameUtil';

type TabValue = 'overall' | 'divisions' | 'playoffs';

const PLAYOFF_PICTURE_PHASES = new Set([LeaguePhase.Playoffs, LeaguePhase.SeasonComplete])

interface GamesByWeek {
  [weekNumber: string]: LeagueGame[];
}

interface DisplayTeamProps {
  teamId: string;
  leagueId: string;
  team: Team;
  isUserTeam: boolean;
}

export interface AllTeamsForSeasonProps {
  teamSeasons: TeamSeason[];
  league: League;
  leagueSeason: LeagueSeason;
  joinEnabled?: boolean;
  onJoinSuccess?: () => void;
}

const AllTeamsForSeason: React.FC<AllTeamsForSeasonProps> = ({
  teamSeasons,
  league,
  leagueSeason,
  joinEnabled = false,
  onJoinSuccess,
}) => {
  const [activeTab, setActiveTab] = React.useState<TabValue>(
    PLAYOFF_PICTURE_PHASES.has(league.phase) ? 'playoffs' : (!leagueSeason?.divisions?.length ? 'overall' : 'divisions')
  );
  
  const handleJoinSuccess = () => {
    onJoinSuccess?.();
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: TabValue) => {
    setActiveTab(newValue);
  };
  
  const user = useAppSelector((state) => state.user);
  const playerMap :  Map<string | undefined, Player | undefined> = usePlayerDataLoadFlat();
  const nflGames = useAppSelector((state) => state.nflGameDataLoad );
  const nflGameMap = React.useMemo(() => 
      new Map(nflGames.map(nflGame => [nflGame.id!, nflGame])), 
      [nflGames]
    );
  
  const gamesByWeek: GamesByWeek = React.useMemo(
      () => (league?.currentSeason?.schedule || []).reduce<GamesByWeek>((acc, game) => {
        if (!game.weekNumber) return acc;
        const week = game.weekNumber.toString();
        if (!acc[week]) {
          acc[week] = [];
        }
        const scoredGame = loadGameRosterFromQuickstats(league, game, playerMap, nflGameMap);
        acc[week].push(scoredGame);
        return acc;
      }, {}),
      [league?.currentSeason?.schedule]
    );
    
  const DisplayTeam: React.FC<DisplayTeamProps> = ({ team, leagueId, isUserTeam }) => (
    <TeamDisplay 
      leagueId={leagueId} 
      team={team} 
      emphasized={isUserTeam} 
      teamId={undefined}
    />
  );
  
  const teamMap = React.useMemo(
      () => new Map(league?.teams?.map(team => [team.id, team]) || []),
      [league?.teams]
    );
  
  const displayTeam = (teamId: string) => {
      const team = teamMap.get(teamId);
      if (!team) return null;
      
      return (
        <DisplayTeam
          teamId={teamId}
          leagueId={leagueId!}
          team={team}
          isUserTeam={team.owners?.some(owner => owner.id === user.id) ?? false}
        />
      );
    };

  if (!leagueSeason) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  const hasDivisions = !!leagueSeason.divisions?.length;
  const leagueId = league?.id || '';

  if (!hasDivisions && !PLAYOFF_PICTURE_PHASES.has(league.phase)) {
    return (
      <Box sx={{ mt: 2 }}>
        <OverallStandings 
          teamSeasons={teamSeasons}
          leagueId={leagueId}
          joinEnabled={joinEnabled}
          onJoinSuccess={handleJoinSuccess}
          leagueSeason={leagueSeason}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          aria-label="standings view tabs"
          variant="scrollable"
          scrollButtons="auto"
        >
          {PLAYOFF_PICTURE_PHASES.has(league.phase) && <Tab label="Playoff Bracket" value={"playoffs"} />}
          {hasDivisions && <Tab label="Division Standings" value="divisions" />}
          <Tab label="Overall Standings" value="overall" />
        </Tabs>
      </Box>

      {activeTab === 'overall' && (
        <OverallStandings 
          teamSeasons={teamSeasons}
          leagueId={leagueId}
          joinEnabled={joinEnabled}
          onJoinSuccess={handleJoinSuccess}
          leagueSeason={leagueSeason}
          waiverPriorityOrder={league?.waiverPriorityOrder!}
        />
      )}
      {activeTab === 'divisions' && (
        <DivisionStandings 
          teamSeasons={teamSeasons}
          leagueId={leagueId}
          joinEnabled={joinEnabled}
          onJoinSuccess={handleJoinSuccess}
          leagueSeason={leagueSeason}
          waiverPriorityOrder={league?.waiverPriorityOrder!}
        />
      )}
      {activeTab === 'playoffs' && (
        <PlayoffBracket
          leagueId={leagueId!}
          gamesByWeek={gamesByWeek}
          displayTeam={displayTeam}
          teams={league.teams}
          finalWeekOfRegularSeason={leagueSeason.finalWeekOfRegularSeason!}
          playoffRoundConfig={leagueSeason.playoffRoundConfig!}
        />
      )}
    </Box>
  );
};

export default AllTeamsForSeason;